import { TagGetViewModel, customerApis } from "@payping/api";
import { commonConstants } from "@payping/utils/platformConstants";
import { ensureString } from "@payping/utilities-shared/string";
import { ValidPostalCode } from "@payping/utils/validation";
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  Message,
  Modal,
  Row,
  Select,
  Space,
  Tooltip,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { FormInstance } from "@payping/web-components/components/Form/types";
import { Add, Delete } from "@payping/icons";
import React, { ChangeEvent, useState } from "react";
import { useAsyncRetry } from "react-use";

import { useAppTranslation } from "@/utils/useAppTranslation";
import styles from "@/assets/scss/dashCustomer/form.module.scss";
import { CustomerFormTypes } from "../types";

interface BaseFormProps {
  loading?: boolean;
  form: FormInstance<CustomerFormTypes>;
  className?: string;
}

const ExpandForm: React.FC<BaseFormProps> = (props: BaseFormProps) => {
  const { loading, form, className } = props;
  const [newTag, setNewTag] = useState<string>("");
  const { customerTagGet, customerTagPost, customerTagDelete } = customerApis;
  const [categories, setCategories] = useState<TagGetViewModel[]>([]);
  const { loading: loadingCategories } = useAsyncRetry(async () => {
    const tags = await customerTagGet();
    setCategories(tags);
  }, []);
  const { t } = useAppTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const [isAddTagModalOpen, setIsAddTagModalOpen] = useState<boolean>(false);

  const [, addTag] = useMessagedAsyncFn(
    async (): Promise<void> => {
      if (newTag) {
        await customerTagPost({ title: newTag });
        const tags = await customerTagGet();
        setCategories(tags);
        const currentValues = form.getFieldsValue();

        form?.setFieldsValue({
          ...currentValues,
          tags: [...(currentValues.tags ?? []), tags.find((tag) => tag.title === newTag)?.id].filter(
            (i) => i !== undefined,
          ),
        });
        setNewTag("");
        Message.success({
          content: t("message.success", { operation: t("common.create"), subject: t("category") }),
        });
      }
    },
    [newTag, form],
    undefined,
    { useProgress: true },
  );

  async function deleteTag(
    event: React.MouseEvent<SVGSVGElement, MouseEvent>,
    category: TagGetViewModel,
  ): Promise<void> {
    event.stopPropagation();
    modal.confirm({
      cancelText: t("cancel"),
      content: t("message.removeCategory", { subject: category.title }),
      maskClosable: true,
      okText: t("ok"),
      onOk: async () => {
        if (category.title) {
          await customerTagDelete(category.id);
          const tags = await customerTagGet();
          setCategories(tags);
          setNewTag("");
        }
      },
    });
  }

  return (
    <div className={className}>
      <Form.Item label={t("customer.form.tag.select")}>
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={18} lg={12}>
            <Form.Item noStyle shouldUpdate>
              {(formTag) => (
                <>
                  <Select
                    value={formTag.getFieldValue("tags")}
                    onChange={(val) => formTag.setFieldValue("tags", val)}
                    skeleton={loadingCategories}
                    mode={"multiple"}
                    allowClear
                    showArrow={true}
                    dropdownRender={(menu) => (
                      <>
                        <div className={"tw-w-full !tw-py-0"}>
                          <Button
                            type={"link"}
                            icon={<Add width={14} height={14} />}
                            className={"tw-w-full"}
                            onClick={() => setIsAddTagModalOpen(true)}
                          >
                            {t("product.form.tag.add")}
                          </Button>
                        </div>

                        <Divider className={"!tw-m-0"} />
                        {menu}
                      </>
                    )}
                    popupClassName={styles.categoryDropdown}
                    optionLabelProp={"title"}
                    placeholder={t("product.form.tag.select")}
                    filterOption={(input, option): boolean => (option?.title as unknown as string).indexOf(input) >= 0}
                    options={categories.map((category, index) => ({
                      key: index,
                      label: (
                        <Space className={styles.categoryOption}>
                          <Tooltip title={t("delete")}>
                            <Delete
                              className={styles.categoryDelete}
                              onClick={(event) => {
                                deleteTag(event, category);
                              }}
                            />
                          </Tooltip>
                          {category.title}
                        </Space>
                      ),
                      title: ensureString({ text: category.title ?? "", fallbackValue: "-" }),
                      value: category.id || "",
                    }))}
                  />
                </>
              )}
            </Form.Item>
            <Modal
              title={t("product.form.tag.add")}
              open={isAddTagModalOpen}
              onCancel={() => setIsAddTagModalOpen(false)}
              okButtonProps={{ loading }}
              onOk={async () => {
                await addTag();
                setIsAddTagModalOpen(false);
              }}
            >
              <Input
                skeleton={loading}
                value={newTag}
                placeholder={t("product.form.tag.add")}
                onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                  setNewTag(event.target.value);
                }}
                autoFocus
              />
            </Modal>
          </Col>
        </Row>
      </Form.Item>
      <Row gutter={[16, 8]}>
        <Col xs={24} lg={12}>
          <Form.Item label={t("state")} shouldUpdate>
            {(formState) => (
              <Select
                showSearch
                filterOption={(input, option): boolean => (option?.children as unknown as string).indexOf(input) >= 0}
                value={formState.getFieldValue("state")}
                onChange={(val) => {
                  formState.setFieldValue("state", val);
                  formState.setFieldValue("city", "");
                }}
              >
                {Object.keys(commonConstants.cities).map((st: string) => (
                  <Select.Option key={st} value={st}>
                    {st}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label={t("city")} shouldUpdate>
            {(formCity) => (
              <Select
                showSearch
                filterOption={(input, option): boolean => (option?.children as unknown as string).indexOf(input) >= 0}
                value={formCity.getFieldValue("city")}
                onChange={(val) => formCity.setFieldValue("city", val)}
              >
                {formCity.getFieldValue("state")
                  ? commonConstants.cities[formCity.getFieldValue("state")]?.map((city: string) => (
                      <Select.Option key={city} value={city}>
                        {city}
                      </Select.Option>
                    ))
                  : null}
              </Select>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label={t("address")} name={"location"}>
            <Input skeleton={loading} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={7}>
          <Form.Item
            label={t("postalCode")}
            name={"zipCode"}
            rules={[
              {
                validator: (_rule, value): Promise<void> =>
                  value && !ValidPostalCode(value) ? Promise.reject(t("error.postalCode")) : Promise.resolve(),
              },
            ]}
          >
            <Input skeleton={loading} onlyNumbers />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item label={t("otherInfo")} name={"additionalInfo"}>
            <Input skeleton={loading} />
          </Form.Item>
        </Col>
        <Col xs={24} lg={12}>
          <Form.Item label={t("reminder")} name={"memo"}>
            <Input skeleton={loading} />
          </Form.Item>
        </Col>
      </Row>

      {contextHolder}
    </div>
  );
};

export default ExpandForm;
